import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Header,
  SpaceBetween,
  Table,
  Modal,
  Box,
  FormField,
  Input,
  Textarea,
  BreadcrumbGroup,
  Tabs,
  Autosuggest,
  TextFilter,
  Pagination,
  CollectionPreferences,
} from '@cloudscape-design/components';
import { useAuth } from 'react-oidc-context';
import { Layout } from '../../components/layout';
import { FAQ } from '../../types'
import { getEndpoint } from '../../utils';
import Markdown from 'markdown-to-jsx';
import { SectionsTable } from '../../components/admin/SectionsTable';

export default function ManageFAQs() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [selectedFaqs, setSelectedFaqs] = useState<FAQ[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('faqs');
  const [fetchSectionsFailed, setFetchSectionsFailed] = useState(false);
  const [filteringText, setFilteringText] = useState("");
  const [preferences, setPreferences] = useState({ pageSize: 10 });
  const [pageIndex, setPageIndex] = useState(1);
  const auth = useAuth();
  const navigate = useNavigate();

  const handleExportFAQs = () => {
    const fileData = JSON.stringify(faqs, null, 2);
    const blob = new Blob([fileData], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `faqs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleImportFAQs = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const fileContent = await file.text();
      const importedFaqs: FAQ[] = JSON.parse(fileContent);

      // Filter out FAQs that already exist (matching by question)
      const existingQuestions = new Set(faqs.map(faq => faq.question));
      const newFaqs = importedFaqs.filter(faq => !existingQuestions.has(faq.question));

      // Add each new FAQ
      for (const faq of newFaqs) {
        const { id, createdAt, updatedAt, ...faqData } = faq; // Remove fields that should be generated by the API
        await fetch(getEndpoint('/api/admin/faq'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.user?.id_token}`,
          },
          body: JSON.stringify(faqData),
        });
      }

      // Refresh the FAQ list
      await fetchFAQs();
    } catch (error) {
      console.error('Error importing FAQs:', error);
    }

    // Reset the file input
    event.target.value = '';
  };

  const fetchFAQs = useCallback(async () => {
    if (auth.isAuthenticated) {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(getEndpoint('/api/admin/faq'), {
          headers: {
            Authorization: `Bearer ${auth.user?.id_token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setFaqs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch FAQs');
      } finally {
        setIsLoading(false);
      }
    }
  }, [auth.isAuthenticated, auth.user?.id_token, setFaqs, setIsLoading, setError]);

  const fetchSections = useCallback(async () => {
    if (auth.isAuthenticated) {
      try {
        const sectionsResponse = await fetch(getEndpoint('/api/admin/sections'), {
          headers: {
            Authorization: `Bearer ${auth.user?.id_token}`,
          },
        });
        const sectionsData = await sectionsResponse.json();
        setSections(sectionsData.map((section: any) => section.PK.replace('FAQ#', '')));

      } catch (error) {
        console.error('Error fetching sections:', error);
        setFetchSectionsFailed(true);
        setSections([]);
      }
    }
  }, [auth.isAuthenticated, auth.user?.id_token, setSections, setFetchSectionsFailed]);

  useEffect(() => {
    if (activeTab === 'faqs' && faqs.length === 0) {
      fetchFAQs();
    }
    if (activeTab === 'sections' && sections.length === 0 && !fetchSectionsFailed) {
      fetchSections();
    }
  }, [activeTab, faqs.length, sections.length, fetchFAQs, fetchSections, fetchSectionsFailed]);


  const handleDelete = async () => {
    if (selectedFaqs.length > 0) {
      try {
        await Promise.all(
          selectedFaqs.map((faq) =>
            fetch(getEndpoint(`/api/admin/faq/${faq.id}`), {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${auth.user?.id_token}`,
              },
            })
          )
        );
        await fetchFAQs();
        setSelectedFaqs([]);
        setShowDeleteModal(false);
      } catch (error) {
        console.error('Error deleting FAQs:', error);
      }
    }
  };

  const handleEdit = async () => {
    if (editingFaq) {
      try {
        // Need to handle section change by deleting old and creating new
        const oldSection = selectedFaqs[0].section;
        if (oldSection !== editingFaq.section) {
          // Delete from old section
          await fetch(getEndpoint(`/api/admin/faq/${editingFaq.id}`), {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.user?.id_token}`,
            },
            body: JSON.stringify({ section: oldSection }),
          });

          // Create in new section
          await fetch(getEndpoint('/api/admin/faq'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.user?.id_token}`,
            },
            body: JSON.stringify({
              section: editingFaq.section,
              question: editingFaq.question,
              answer: editingFaq.answer,
            }),
          });
        } else {
          // Normal update if section hasn't changed
          await fetch(getEndpoint(`/api/admin/faq/${editingFaq.id}`), {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.user?.id_token}`,
            },
            body: JSON.stringify(editingFaq),
          });
        }
        await fetchFAQs();
        setShowEditModal(false);
        setEditingFaq(null);
      } catch (error) {
        console.error('Error updating FAQ:', error);
      }
    }
  };

  const breadcrumbs = (
    <BreadcrumbGroup
      onClick={({ detail }) => navigate(detail.href)}
      items={[
        { text: "Admin", href: "/admin" },
        { text: "Manage FAQs", href: `/admin/faqs/` }
      ]}
    />
  )

  const filteredFaqs = faqs.filter((faq) =>
    faq.question?.toLowerCase().includes(filteringText.toLowerCase())
  );
  const startIndex = (pageIndex - 1) * preferences.pageSize;
  const endIndex = startIndex + preferences.pageSize;
  const currentPageFaqs = filteredFaqs.slice(startIndex, endIndex);

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <SpaceBetween direction="vertical" size="l">
        <Header variant="h1">Manage FAQs</Header>

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            {
              label: "FAQs",
              id: "faqs",
              content: (
                <SpaceBetween direction="vertical" size="l">
                  <Header
                    actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button onClick={() => navigate('/admin/faq/new')}>Add FAQ</Button>
                        <Button
                          disabled={selectedFaqs.length !== 1}
                          onClick={() => {
                            setEditingFaq(selectedFaqs[0]);
                            setShowEditModal(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          disabled={selectedFaqs.length === 0}
                          onClick={() => setShowDeleteModal(true)}
                        >
                          Delete
                        </Button>
                        <Button onClick={handleExportFAQs}>Export FAQs</Button>
                        <div style={{ display: 'inline-block' }}>
                          <input
                            type="file"
                            id="import-faqs"
                            accept=".json"
                            style={{ display: 'none' }}
                            onChange={handleImportFAQs}
                          />
                          <Button onClick={() => document.getElementById('import-faqs')?.click()}>
                            Import FAQs
                          </Button>
                        </div>
                      </SpaceBetween>
                    }
                  >
                    FAQs
                  </Header>

                  <Table
                    loading={isLoading}
                    loadingText="Loading FAQs..."
                    items={currentPageFaqs}
                    selectedItems={selectedFaqs}
                    onSelectionChange={({ detail }) => setSelectedFaqs(detail.selectedItems)}
                    selectionType="multi"
                    empty={
                      <Box textAlign="center" color="inherit">
                        {error ? (
                          <SpaceBetween size="m">
                            <div>Error loading FAQs: {error}</div>
                            <Button onClick={() => fetchFAQs()}>Retry</Button>
                          </SpaceBetween>
                        ) : (
                          <div>No FAQs found</div>
                        )}
                      </Box>
                    }
                    columnDefinitions={[
                      {
                        id: 'section',
                        header: 'Section',
                        cell: (item) => item.section,
                      },
                      {
                        id: 'question',
                        header: 'Question',
                        cell: (item) => item.question,
                      },
                      {
                        id: 'answer',
                        header: 'Answer',
                        cell: (item) => item.answer,
                      },
                    ]}
                    filter={
                      <TextFilter
                        filteringPlaceholder="Search by question"
                        filteringText={filteringText}
                        onChange={({ detail }) => {
                          setFilteringText(detail.filteringText);
                          setPageIndex(1);
                        }}
                      />
                    }
                    preferences={
                      <CollectionPreferences
                        title="Preferences"
                        confirmLabel="Confirm"
                        cancelLabel="Cancel"
                        pageSizePreference={{
                          title: "Select page size",
                          options: [
                            { value: 5, label: "5 resources" },
                            { value: 10, label: "10 resources" },
                            { value: 25, label: "25 resources" }
                          ]
                        }}
                        preferences={{ pageSize: preferences.pageSize }}
                        onConfirm={({ detail }) => {
                          setPreferences({...preferences, pageSize: detail.pageSize ?? 10});
                          setPageIndex(1);
                        }}
                      />
                    }
                    pagination={
                      <Pagination
                        currentPageIndex={pageIndex}
                        pagesCount={Math.ceil(filteredFaqs.length / preferences.pageSize)}
                        onChange={({ detail }) => setPageIndex(detail.currentPageIndex)}
                      />
                    }
                  />
                </SpaceBetween>
              )
            },
            {
              label: "Sections",
              id: "sections",
              content: (
                <SectionsTable />
              )
            }
          ]}
        />

        <Modal
          visible={showDeleteModal}
          onDismiss={() => setShowDeleteModal(false)}
          header="Delete FAQs"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleDelete}>
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          Are you sure you want to delete the selected FAQs?
        </Modal>

        <Modal
          visible={showEditModal}
          onDismiss={() => {
            setShowEditModal(false);
            setEditingFaq(null);
          }}
          header="Edit FAQ"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingFaq(null);
                  }}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleEdit}>
                  Save
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {editingFaq && (
            <SpaceBetween direction="vertical" size="l">
              <FormField label="Section">
                <Autosuggest
                  value={editingFaq.section}
                  onChange={({ detail }) =>
                    setEditingFaq({
                      ...editingFaq,
                      section: detail.value,
                    })
                  }
                  onSelect={({ detail }) =>
                    setEditingFaq({
                      ...editingFaq,
                      section: detail.value,
                    })
                  }
                  options={sections.map(section => ({ value: section }))}
                  enteredTextLabel={value => `Use "${value}"`}
                  placeholder="Enter a section name or select an existing one"
                  empty="No matching sections found"
                />
              </FormField>
              <FormField label="Question">
                <Input
                  value={editingFaq.question}
                  onChange={({ detail }) =>
                    setEditingFaq({
                      ...editingFaq,
                      question: detail.value,
                    })
                  }
                />
              </FormField>
              <FormField label="Answer">
                <Tabs
                  tabs={[
                    {
                      label: "Edit",
                      id: "edit",
                      content: (
                        <Textarea
                          value={editingFaq.answer}
                          onChange={({ detail }) =>
                            setEditingFaq({
                              ...editingFaq,
                              answer: detail.value,
                            })
                          }
                        />
                      )
                    },
                    {
                      label: "Preview",
                      id: "preview",
                      content: (
                        <Box color="text-body-secondary">
                          <Markdown>{editingFaq.answer}</Markdown>
                        </Box>
                      )
                    }
                  ]}
                />
              </FormField>
            </SpaceBetween>
          )}
        </Modal>
      </SpaceBetween>
    </Layout >
  );
}
